/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Props } from '../types';
import { PageStateContext } from '../../services/pageStore';

type ProductCatalogFields = {
  'action-link'?: string;
  'catalog-title'?: string;
  subline?: string;
};

type ProductCatalogProps = Props<ProductCatalogFields> & {
  activeCategory: string;
  onCategoryChange?;
  children;
}

export const RocheProductCatalog = ({
  blok,
  activeCategory,
  onCategoryChange,
  children,
}: ProductCatalogProps): JSX.Element => {
  const pageContext = useContext(PageStateContext);
  const pageSettings = pageContext?.settings;
  const URL = blok?.action_link?.story?.url ? `/${blok?.action_link?.story?.url}` : blok?.action_link?.url;
  const [categoriesDropdownData, setCategoriesDropdownData] = useState([]);
  const [subCategoriesDropdownData, setSubCategoriesDropdownData] = useState([]);

  const { storyblok } = useStaticQuery(graphql`
    {
      storyblok {
        qSubCategories: ProductcategoryItems {
          items {
            name
            parent_id
            id
            uuid
          }
        }
        qCategories: Links {
          items {
            name
            id
            parentId
            isFolder
          }
        }
      }
    }
  `);

  const getCategories = () => {
    // build map of categories
    const categoriesMap = storyblok.qCategories.items.reduce((acc, category) => {
      if (category.isFolder) {
        acc[category.id] = { ...category, ...{ subCategories: [] } };
      }
      return acc;
    }, {});

    // add subcategories to categories
    storyblok.qSubCategories.items.forEach((subCategory) => {
      const sCat = categoriesMap[subCategory.parent_id].subCategories || [];
      sCat.push(subCategory);
      categoriesMap[subCategory.parent_id].subCategories = sCat;
    });

    // remove emtpy
    Object.keys(categoriesMap).forEach((categoryId) => {
      if (categoriesMap[categoryId].subCategories.length === 0) {
        delete categoriesMap[categoryId];
      }
    });

    const [selectedCategory, selectedSubCategory] = activeCategory?.split(',') || [];

    // categories
    const categoriesDropdown = Object.keys(categoriesMap).map((key) => {
      const category = categoriesMap[key];
      return {
        label: category.name,
        value: category.id,
        selected: category.id === +selectedCategory,
      };
    });

    setCategoriesDropdownData(categoriesDropdown);

    // subcategories
    const subCategoriesDropdown = Object.keys(categoriesMap).map((key) => {
      const category = categoriesMap[key];

      const subCategories = category.subCategories.map((subCategory) => ({
        label: subCategory.name,
        value: subCategory.uuid,
        selected: subCategory.uuid === selectedSubCategory,
      }));

      return [...[{
        label: '',
        value: '',
        selected: false,
      }], ...subCategories];
    });

    setSubCategoriesDropdownData(subCategoriesDropdown);
  };

  useEffect(() => {
    getCategories();
    document.addEventListener('rocheProductCatalogSelect', onCategoryChange);

    return (): void => document.removeEventListener('rocheProductCatalogSelect', onCategoryChange);
  }, []);

  return React.createElement('roche-product-catalog', {
    'action-link': URL,
    subline: blok?.subline,
    'catalog-title': blok?.catalog_title,
    'first-dropdown-placeholder': pageSettings?.product_catalog_dropdown_category_placeholder || '',
    'second-dropdown-placeholder': pageSettings?.product_catalog_dropdown_subcategory_placeholder || '',
    'first-dropdown-filter-data': JSON.stringify(categoriesDropdownData),
    'second-dropdown-filter-data': JSON.stringify(subCategoriesDropdownData),
  }, children);
};
